import { apiClient } from './client';

/**
 * Client for the AI integration scaffolder (ADR-034).
 *
 * Stays in lockstep with `server/src/routes/integrations/scaffold.routes.ts`.
 * The scaffolder lets an admin connect *any* REST/JSON API: paste the API
 * docs, let AI propose a wiring spec, run a live sample call, then save it as
 * a generic custom provider the existing sync engine can run.
 *
 * Three endpoints back the four-step wizard:
 *   - `POST /api/integrations/scaffold/propose` — AI drafts a {@link ProviderSpec}.
 *     Degrades to 503 `ai_disabled` when AI isn't configured; that case is
 *     surfaced as a typed {@link ScaffolderAiDisabledError} so the wizard can
 *     show a friendly "AI isn't configured" notice instead of an error.
 *   - `POST /api/integrations/scaffold/test` — the live sample call: one guarded
 *     request against the external API to prove the spec works before saving.
 *   - `POST /api/integrations/scaffold/save` — persists the reviewed provider
 *     and, optionally, a connected `Integration` row.
 */

// ── Shared spec types — mirror `server/src/integrations/custom/spec.ts` ──

export type MetricDirection = 'higher_is_better' | 'lower_is_better';
export type ScaffolderAuthKind = 'none' | 'api_key' | 'bearer' | 'basic';

export interface MetricSpec {
  /** Slug matching `Metric.dataSourceConfig.metricKey`; `^[a-z0-9_]+$`, 1..64. */
  key: string;
  label: string;
  direction: MetricDirection;
  /** MVP: GET only. */
  method: 'GET';
  /** Relative to baseUrl, starts with `/`; may contain `{weekStart}`/`{weekEnd}`. */
  path: string;
  /** Static query params; values may contain the week placeholders. */
  query?: Record<string, string>;
  /** Dotted path to a number in the JSON body, e.g. `data.totals.shipped`. */
  valuePath: string;
}

export interface ProviderSpec {
  /** Where the credential is injected on each request. */
  auth: { in: 'header' | 'query'; name: string; scheme?: string };
  metrics: MetricSpec[];
}

/**
 * Credentials the admin supplies for the live test / connect. The server maps
 * these onto a `ConnectionConfig` for the chosen auth kind: `api_key`/`bearer`
 * read `apiKey`; `basic` reads `username`/`password`; `none` needs nothing.
 */
export interface ScaffolderCredentials {
  apiKey?: string;
  username?: string;
  password?: string;
}

// ── Request / response shapes ──

export interface ProposeIntegrationRequest {
  docsText: string;
  baseUrl: string;
  category?: string;
  authKind?: ScaffolderAuthKind;
}

export interface TestIntegrationMetricRequest {
  spec: ProviderSpec;
  metricKey: string;
  baseUrl: string;
  authKind: ScaffolderAuthKind;
  credentials: ScaffolderCredentials;
}

export interface TestIntegrationMetricResult {
  /** HTTP status of the sample call. */
  status: number;
  /** Extracted numeric value, or null when extraction failed. */
  value: number | null;
  /** Present when the value couldn't be read — the human fixes `valuePath`. */
  extractError?: string;
  /** Truncated body preview so the human can locate the value. */
  responsePreview: string;
}

export interface SaveIntegrationRequest {
  name: string;
  displayName: string;
  category: string;
  baseUrl: string;
  authKind: ScaffolderAuthKind;
  spec: ProviderSpec;
  /** When present, also creates a connected `Integration` with these creds. */
  connect?: { credentials: ScaffolderCredentials };
}

export interface SavedCustomProvider {
  id: string;
  name: string;
  displayName: string;
  category: string;
  baseUrl: string;
  authKind: ScaffolderAuthKind;
  status: string;
}

export interface SaveIntegrationResult {
  provider: SavedCustomProvider;
  /** Set when `connect` was requested — the id of the created Integration. */
  integrationId?: string;
}

// ── Typed errors ──

/** Thrown when the server reports AI is not configured (503 `ai_disabled`). */
export class ScaffolderAiDisabledError extends Error {
  constructor() {
    super('AI assistance is not configured for this organization.');
    this.name = 'ScaffolderAiDisabledError';
  }
}

/** True when an error is the 503 `ai_disabled` envelope. */
function isAiDisabled(err: unknown): boolean {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
    return response?.status === 503 && response.data?.error === 'ai_disabled';
  }
  return false;
}

// ── Endpoints ──

/**
 * Ask the model to draft a {@link ProviderSpec} from pasted API docs. Resolves
 * with the proposed spec, throws {@link ScaffolderAiDisabledError} when AI is
 * switched off, and otherwise re-throws the axios error (carrying
 * `ai_could_not_propose` on 422 / a server error on 502) for the caller to map.
 */
export async function proposeIntegration(body: ProposeIntegrationRequest): Promise<ProviderSpec> {
  try {
    const res = await apiClient.post<{ data: { spec: ProviderSpec } }>(
      '/api/integrations/scaffold/propose',
      body,
    );
    return res.data.data.spec;
  } catch (err) {
    if (isAiDisabled(err)) throw new ScaffolderAiDisabledError();
    throw err;
  }
}

/**
 * Run the live sample call for one metric. This makes a REAL request to the
 * external API. The server catches SSRF errors and returns 400 `unsafe_url`.
 */
export async function testIntegrationMetric(
  body: TestIntegrationMetricRequest,
): Promise<TestIntegrationMetricResult> {
  const res = await apiClient.post<{ data: TestIntegrationMetricResult }>(
    '/api/integrations/scaffold/test',
    body,
  );
  return res.data.data;
}

/**
 * Persist the reviewed provider (and, when `connect` is set, a connected
 * Integration). Rejects with a 409 axios error when `name` already exists.
 */
export async function saveIntegration(body: SaveIntegrationRequest): Promise<SaveIntegrationResult> {
  const res = await apiClient.post<{ data: SaveIntegrationResult }>('/api/integrations/scaffold/save', body);
  return res.data.data;
}
